import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { MenuItem } from "primeng/api";
import { TimezoneModel, TimezoneService } from "ng/src/app/log-settings/timezone.service";
import { finalize, map } from "rxjs/operators";
import { LogService } from "ng/src/app/logs/log.service";
import { Inplace } from "primeng/inplace";
import { Log } from "ng/src/app/logs/log.model";

@Component({
	selector: 'lb-timezone-selector[log]',
	templateUrl: './timezone-selector.component.html',
	styleUrls: ['./timezone-selector.component.scss']
})
export class TimezoneSelectorComponent implements OnInit {
	@ViewChild('content') inplace!: Inplace;
	@Input() log!: Log;
	
	currentZoneLabel = '';
	timezoneModel: MenuItem[] = [];
	loadingZones = false;
	updatingLog = false;
	
	confirmingZone?: TimezoneModel;
	showingDialog = false;
	
	get canEdit() {
		return LogService.selectedLog?.role === 'ADMIN';
	}
	
	constructor(
		private timezoneService: TimezoneService,
		public logService: LogService
	) {}
	
	ngOnInit() {
		this.updateLabel(this.log.timeZone);
	}
	
	updateZones() {
		this.loadingZones = true;
		this.timezoneService.getTimezones().pipe(
				map(res => this.createModel(res)),
				finalize(() => this.loadingZones = false)
			)
			.subscribe(items => {
				this.timezoneModel = items;
			});
	}
	
	updateTimezone() {
		const newZone = this.confirmingZone;
		if (!newZone) return;
		this.updatingLog = true;
		this.logService.updateLogTimeZone(newZone, this.log)
			.pipe(
				finalize(() => {
					this.updatingLog = false;
					this.showingDialog = false;
					this.inplace.deactivate();
				})
			)
			.subscribe(update => {
				this.log.timeZone = newZone.id;
				this.updateLabel(newZone.id);
			});
	}
	
	formatLabel(label: string) {
		return label.replace(/_/g, ' ');
	}
	
	private updateLabel(label: string) {
		this.currentZoneLabel = this.formatLabel(label);
	}
	
	private confirmChange(newTimezone: TimezoneModel) {
		this.confirmingZone = newTimezone;
		this.showingDialog = true;
	}
	
	private createModel(timezoneRecords: TimezoneModel[]): MenuItem[] {
		const otherEntries: MenuItem = {
			label: 'Other',
			items: []
		};
		const items = timezoneRecords.reduce((items, entry) => {
			const menuEntry: MenuItem = this.createEntry(entry);
			const entrySet = entry.id.split('/');
			
			let currentItem: MenuItem | null = null;
			
			Object.values(entrySet).forEach((key, idx, src) => {
				const keyDetailEntry = { id: key, label: this.formatLabel(key) }
				
				if (currentItem == null) {
					if (idx >= src.length - 1) {
						otherEntries.items?.push(menuEntry);
					}
					else {
						currentItem = this.getMenuEntry(items, keyDetailEntry);
					}
				}
				else if (idx >= src.length - 1) {
					currentItem.items?.push(menuEntry);
				}
				else {
					currentItem = this.getMenuEntry(currentItem, keyDetailEntry);
				}
			})
			return items;
			
		}, [] as MenuItem[]);
		
		items.push(otherEntries);
		return items;
	}
	
	private createEntry(record: TimezoneModel | { id: string, label: string}): MenuItem {
		if ('label' in record) {
			return {
				id: record.id,
				label: record.label,
				items: []
			}
		}
		
		const current = record.id === this.log.timeZone;
		const idSet = record.id.split('/');
		const label = `${current ? '(Current) ' : ''}${this.formatLabel(idSet[idSet.length - 1])} (${record.displayName})`;
		
		return {
			id: record.id,
			label: label,
			disabled: current,
			command: () => this.confirmChange(record)
		}
	}
	
	private getMenuEntry(root: MenuItem | MenuItem[], details: { id: string, label: string }) {
		const items = Array.isArray(root) ? root : root.items!;
		let item = items.find(item => item.id === details.id);
		if (item) return item;
		item = this.createEntry(details);
		items.push(item)
		return item;
	}
}